// personnelMetrics.js — Personnel Metrics page
// Layout mirrors Module Metrics: RTLS map (left) + 3-section right panel.
// Sections: Actions, Personnel Information, Personnel Metrics.
// Activity History and Shift Summary open modals.

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import RtlsMap from "./components/rtlsMap";
import Modal from "./components/Modal";
import PersonnelActivityHistory from "./components/modalContent/personnelMetricsModalComponents/personnelActivityHistory";
import ShiftSummary from "./components/modalContent/personnelMetricsModalComponents/shiftSummary";
import MOCK_PERSONNEL from "./mockData/mockPersonnelData";
import { useIsMobile } from "./hooks/useIsMobile";
import DisabledTooltip from "./components/disabledToolTip";
import "./personnelMetrics.css";
import "../animation.css";

// ─── Function button definitions ─────────────────────────────────────────────

const FUNCTION_BUTTONS = [
  {
    id: "heatmap",
    label: "Heatmap",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="3" />
        <circle cx="12" cy="12" r="7" opacity="0.5" />
        <circle cx="12" cy="12" r="10" opacity="0.25" />
      </svg>
    ),
  },
  {
    id: "spaghetti",
    label: "Spaghetti Map",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
        <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
      </svg>
    ),
  },
  {
    id: "workflow",
    label: "Workflow Map",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="3" y="3" width="5" height="5" rx="1" />
        <rect x="16" y="3" width="5" height="5" rx="1" />
        <rect x="3" y="16" width="5" height="5" rx="1" />
        <rect x="16" y="16" width="5" height="5" rx="1" />
        <line x1="8" y1="5.5" x2="16" y2="5.5" />
        <line x1="5.5" y1="8" x2="5.5" y2="16" />
        <line x1="18.5" y1="8" x2="18.5" y2="16" />
        <line x1="8" y1="18.5" x2="16" y2="18.5" />
      </svg>
    ),
  },
  {
    id: "playback",
    label: "Playback",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
  {
    id: "activity",
    label: "Activity History",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    id: "shift",
    label: "Shift Summary",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <line x1="8" y1="14" x2="10" y2="14" />
        <line x1="12" y1="14" x2="16" y2="14" />
        <line x1="8" y1="18" x2="10" y2="18" />
      </svg>
    ),
  },
];

// ─── Battery helper ───────────────────────────────────────────────────────────

const batteryColor = (pct) =>
  pct > 60 ? "var(--pm-green)" : pct > 20 ? "var(--pm-amber)" : "var(--pm-red)";

const batteryBg = (pct) =>
  pct > 60
    ? "rgba(56,161,105,0.12)"
    : pct > 20
      ? "rgba(214,158,46,0.12)"
      : "rgba(204,0,0,0.10)";

const batteryBorder = (pct) =>
  pct > 60
    ? "rgba(56,161,105,0.3)"
    : pct > 20
      ? "rgba(214,158,46,0.3)"
      : "rgba(204,0,0,0.25)";

const batteryLabel = (pct) =>
  pct > 60 ? "Good" : pct > 20 ? "Low" : "Critical";

// ─── Main page component ─────────────────────────────────────────────────────

const PersonnelMetricsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [activeModal, setActiveModal] = useState(null);

  // On mobile (≤480px): show only the 2 modal-backed buttons (Activity History,
  // Shift Summary). The 4 RTLS-API buttons are hidden — they depend on the
  // embedded map which is also hidden on mobile (see conditional render below).
  const isMobile = useIsMobile();
  const visibleButtons = isMobile
    ? FUNCTION_BUTTONS.filter((btn) => btn.modal)
    : FUNCTION_BUTTONS;

  // Handle URL parameter for auto-loading a person
  useEffect(() => {
    const nameParam = searchParams.get("name");
    if (nameParam && !selectedPerson) {
      // Find person by full name
      const person = MOCK_PERSONNEL.find((p) => p.fullName === nameParam);
      if (person) {
        setSearch(nameParam);
        setSelectedPerson(person);
        // Clear URL params after loading to avoid re-triggering
        setSearchParams({});
      }
    }
  }, [searchParams, selectedPerson, setSearchParams]);

  // ── CHANGED: Return top 10 personnel when query is empty (default list),
  //            otherwise filter normally by full name.
  const filteredPersonnel = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return MOCK_PERSONNEL.slice(0, 10);
    return MOCK_PERSONNEL.filter((p) => p.fullName.toLowerCase().includes(q));
  }, [search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setDropdownOpen(true);
  };

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setSearch(person.fullName);
    setDropdownOpen(false);
  };

  const handleFunctionBtn = useCallback(
    (btnId) => {
      if (!selectedPerson) return;
      if (btnId === "activity" || btnId === "shift") {
        setActiveModal(btnId);
        return;
      }
      // console.log(
      //   `RTLS function triggered: ${btnId} for`,
      //   selectedPerson.fullName,
      // );
    },
    [selectedPerson],
  );

  // Build the RIoT search query for the selected person.
  // null  → no filter call (map shows all objects).
  // query → hides every object whose name doesn't match the employee's name.
  const personnelFilterQuery = useMemo(() => {
    if (!selectedPerson) return null;
    // RIoT object names for personnel typically match the tag label / full name.
    // Adjust the field key ("name") to match what your RIoT instance indexes.
    return `name:"${selectedPerson.fullName}"`;
  }, [selectedPerson]);

  const closeModal = useCallback(() => setActiveModal(null), []);

  // Derived battery colours for the selected person
  const bPct = selectedPerson ? selectedPerson.tagBatteryPct : 0;
  const bColor = batteryColor(bPct);
  const bBg = batteryBg(bPct);
  const bBorder = batteryBorder(bPct);
  const bLabel = batteryLabel(bPct);

  // TASK 1 FIX: Remove pace labels - just show numeric value with color coding
  const jobsColor =
    selectedPerson && selectedPerson.indirectMinutes >= 5
      ? "var(--pm-amber)"
      : "var(--pm-green)";

  return (
    <div className="pm-page page-enter">
      <div className="pm-page-header">
        <h1>Personnel Metrics</h1>
        <p className="pm-page-subtitle">
          Search and inspect individual personnel location and production data
        </p>
      </div>

      <div className="pm-layout">
        {/* ── Left: RTLS Map (desktop only) ──────────────────────
            On mobile the map is hidden here and available via the
            dedicated /rtls-map route in the hamburger menu.       */}
        {!isMobile && (
          <div className="pm-map-col">
            <RtlsMap isHomePage={false} filterQuery={personnelFilterQuery} />
          </div>
        )}

        {/* ── Right: 3-section panel ──────────────────────────── */}
        <div className="pm-right-col">
          {/* Search */}
          <div
            className="pm-card pm-search-card stagger-item"
            style={{ "--i": 0 }}
          >
            <div className="pm-search-wrap">
              <div className="pm-search-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                className="pm-search-input"
                type="text"
                placeholder="Search by full name..."
                value={search}
                onChange={handleSearchChange}
                // ── CHANGED: Always open the dropdown on focus so the top-10
                //            default list is immediately visible.
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                aria-label="Search personnel"
              />
              {search && (
                <button
                  className="pm-search-clear"
                  onClick={() => {
                    setSearch("");
                    setDropdownOpen(false);
                    setSelectedPerson(null);
                  }}
                  aria-label="Clear search"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            {dropdownOpen && filteredPersonnel.length > 0 && (
              <ul className="pm-dropdown" role="listbox">
                {/* ── CHANGED: Show a subtle header when displaying the default
                              top-10 list (i.e. no query has been typed yet). */}
                {!search.trim()}
                {filteredPersonnel.map((p) => (
                  <li
                    key={p.employeeId}
                    className="pm-dropdown-item"
                    onMouseDown={() => handleSelectPerson(p)}
                    role="option"
                  >
                    <span className="pm-dropdown-name">{p.fullName}</span>
                    <span className="pm-dropdown-meta">
                      {p.employeeId} &bull; {p.department}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {dropdownOpen &&
              search.trim() &&
              filteredPersonnel.length === 0 && (
                <div className="pm-dropdown pm-dropdown--empty">
                  No personnel match your search.
                </div>
              )}
          </div>

          {/* Empty state */}
          {!selectedPerson && (
            <div className="pm-empty-state stagger-item" style={{ "--i": 1 }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <p>Search for a team member to view details</p>
              <span>Enter a full name above to load personnel data</span>
            </div>
          )}

          {selectedPerson && (
            <>
              {/* Section 1: Actions */}
              <div className="pm-card stagger-item" style={{ "--i": 1 }}>
                <h2 className="pm-section-title">Actions</h2>
                <div className="pm-fn-grid">
                  {visibleButtons.map((btn) => (
                    <DisabledTooltip key={btn.id} disabled={!btn.modal}>
                      <button
                        key={btn.id}
                        className={`pm-fn-btn${btn.modal ? " pm-fn-btn--modal" : ""}`}
                        onClick={() => handleFunctionBtn(btn.id)}
                        title={btn.subtitle}
                      >
                        <div className="pm-fn-btn-icon">{btn.icon}</div>
                        <div className="pm-fn-btn-text">
                          <span className="pm-fn-btn-label">{btn.label}</span>
                          <span className="pm-fn-btn-sub">{btn.subtitle}</span>
                        </div>
                      </button>
                    </DisabledTooltip>
                  ))}
                </div>
              </div>

              {/* Section 2: Personnel Information */}
              <div className="pm-card stagger-item" style={{ "--i": 2 }}>
                <h2 className="pm-section-title">Personnel Information</h2>
                <div className="pm-info-grid">
                  <div className="pm-info-item">
                    <span className="pm-info-label">Full Name</span>
                    <span className="pm-info-value">
                      {selectedPerson.fullName}
                    </span>
                  </div>
                  <div className="pm-info-item">
                    <span className="pm-info-label">Employee Number</span>
                    <span className="pm-info-value">
                      {selectedPerson.employeeId}
                    </span>
                  </div>
                  <div className="pm-info-item">
                    <span className="pm-info-label">Team</span>
                    <span className="pm-info-value">{selectedPerson.team}</span>
                  </div>
                  <div className="pm-info-item">
                    <span className="pm-info-label">Current Zone</span>
                    <span className="pm-info-value">
                      {selectedPerson.currentZone}
                    </span>
                  </div>
                  <div className="pm-info-item">
                    <span className="pm-info-label">Current Job</span>
                    <span className="pm-info-value">
                      {selectedPerson.currentJob}
                    </span>
                  </div>
                  <div className="pm-info-item">
                    <span className="pm-info-label">Last Updated</span>
                    <span className="pm-info-value">
                      {selectedPerson.lastUpdated}
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 3: Personnel Metrics — redesigned dashboard cards */}
              <div className="pm-card stagger-item" style={{ "--i": 3 }}>
                <h2 className="pm-section-title">Personnel Metrics</h2>
                <div className="pm-metrics-grid">
                  {/* ── Card 1: Tag Battery ─────────────────────── */}
                  <div className="pm-metric-tile">
                    <div className="pm-metric-tile-header">
                      <span className="pm-metric-label">Tag Battery</span>
                      <div className="pm-metric-tile-icon">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="2" y="7" width="18" height="10" rx="2" />
                          <path d="M22 11v2" strokeLinecap="round" />
                          <rect
                            x="4"
                            y="9"
                            width={`${Math.round(bPct * 0.14)}`}
                            height="6"
                            rx="1"
                            fill="currentColor"
                            stroke="none"
                            style={{ color: bColor }}
                          />
                        </svg>
                      </div>
                    </div>

                    <div
                      className="pm-metric-value-large"
                      style={{ color: bColor }}
                    >
                      {selectedPerson.tagBatteryPct}
                      <span className="pm-metric-value-unit">%</span>
                    </div>

                    <div className="pm-battery-bar">
                      <div
                        className="pm-battery-fill"
                        style={{
                          width: `${selectedPerson.tagBatteryPct}%`,
                          background: bColor,
                        }}
                      />
                    </div>

                    <span
                      className="pm-metric-status"
                      style={{
                        color: bColor,
                        background: bBg,
                        border: `1px solid ${bBorder}`,
                      }}
                    >
                      {bLabel}
                    </span>
                  </div>

                  {/* ── Card 2: Time Spent in Indirect Activity (NO STATUS LABEL) ────────────── */}
                  <div className="pm-metric-tile">
                    <div className="pm-metric-tile-header">
                      <span className="pm-metric-label">
                        Time Spent in Indirect
                      </span>
                      <div className="pm-metric-tile-icon">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    </div>

                    <div
                      className="pm-metric-value-large"
                      style={{ color: jobsColor }}
                    >
                      {selectedPerson.indirectMinutes}
                    </div>

                    <div className="pm-metric-label-sub">Minutes</div>
                  </div>

                  {/* ── Card 3: Avg Task Duration ────────────────── */}
                  <div className="pm-metric-tile">
                    <div className="pm-metric-tile-header">
                      <span className="pm-metric-label">Avg Task Time</span>
                      <div className="pm-metric-tile-icon">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                      </div>
                    </div>

                    <div className="pm-metric-value-large">
                      {selectedPerson.avgTaskDurationMins}
                      <span className="pm-metric-value-unit">m</span>
                    </div>

                    <div className="pm-metric-label-sub">minutes per task</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Activity History modal */}
      <Modal
        isOpen={activeModal === "activity"}
        onClose={closeModal}
        title="Activity History"
        size="large"
      >
        {selectedPerson && <PersonnelActivityHistory person={selectedPerson} />}
      </Modal>

      {/* Shift Summary modal */}
      <Modal
        isOpen={activeModal === "shift"}
        onClose={closeModal}
        title="Shift Summary"
        size="medium"
      >
        {selectedPerson && <ShiftSummary person={selectedPerson} />}
      </Modal>
    </div>
  );
};

export default PersonnelMetricsPage;
